import { EmailTemplate, EmailTemplateWithEventName, DirectUploadFile } from "@/types/EmailTemplate";
import {
  createEmailTemplate as createEmailTemplateApi,
  getEmailTemplatesByEvent as getEmailTemplatesByEventApi,
  getEmailTemplatesByUserPaginated as getEmailTemplatesByUserPaginatedApi,
  getEmailTemplate as getEmailTemplateApi,
  updateEmailTemplate as updateEmailTemplateApi,
  deleteEmailTemplate as deleteEmailTemplateApi,
  bulkDuplicateEmailTemplates as bulkDuplicateEmailTemplatesApi,
  createTemplateEmailTemplate as createTemplateEmailTemplateApi,
  getTemplateEmailTemplates as getTemplateEmailTemplatesApi,
  getTemplateEmailTemplate as getTemplateEmailTemplateApi,
  updateTemplateEmailTemplate as updateTemplateEmailTemplateApi,
  deleteTemplateEmailTemplate as deleteTemplateEmailTemplateApi,
  presignDirectUpload as presignDirectUploadApi,
  confirmDirectUpload as confirmDirectUploadApi,
  deleteDirectUpload as deleteDirectUploadApi,
  presignTemplateDirectUpload as presignTemplateDirectUploadApi,
  confirmTemplateDirectUpload as confirmTemplateDirectUploadApi,
  deleteTemplateDirectUpload as deleteTemplateDirectUploadApi,
} from "@/lib/api/clients/EmailTemplateClient";
import { handleErrorServer, ServerError } from "@/lib/api/errors/ErrorHandler";
import { isValidObjectId } from "@/lib/utils/input-validator";

export async function createEmailTemplate(
  emailTemplate: EmailTemplate
): Promise<string | ServerError> {
  try {

    return await createEmailTemplateApi(emailTemplate);
  } catch (error) {
    console.error("Error creating email template:", error);
    return handleErrorServer(error);
  }
}

export async function updateEmailTemplate(
  eventId: string,
  emailTemplateId: string,
  emailTemplate: Partial<EmailTemplate>
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId)) {
      throw new Error("validationError");
    }
    if (!isValidObjectId(emailTemplateId)) {
      throw new Error("validationError");
    }
    if (!emailTemplate || Object.keys(emailTemplate).length === 0) {
      throw new Error("validationError");
    }


    return await updateEmailTemplateApi(
      eventId,
      emailTemplateId,
      emailTemplate,
    );
  } catch (error) {
    console.error("Error updating email template:", error);
    return handleErrorServer(error);
  }
}

export async function getEmailTemplatesByEvent(
  eventId: string,
  fields: string
): Promise<EmailTemplate[] | ServerError> {
  try {
    if (!eventId) {
      throw new Error("validationError");
    }

    if (!fields) {
      throw new Error("validationError");
    }


    return await getEmailTemplatesByEventApi(eventId, fields);
  } catch (error) {
    console.error("Error getting email templates by event:", error);
    return handleErrorServer(error);
  }
}

export async function getEmailTemplatesByUserPaginated(
  excludeEventId: string,
  fields: string,
  page: number = 0,
  pageSize: number = 9,
  sortBy: string = "emailTemplateId",
  sortOrder: string = "desc",
  search: string = ""
): Promise<EmailTemplateWithEventName[] | ServerError> {
  try {
    if (!excludeEventId) {
      throw new Error("validationError");
    }

    if (!fields) {
      throw new Error("validationError");
    }

    if (page < 0) {
      throw new Error("validationError");
    }

    if (pageSize <= 0 || pageSize > 150) {
      throw new Error("validationError");
    }


    return await getEmailTemplatesByUserPaginatedApi(
      excludeEventId,
      fields,
      page,
      pageSize,
      sortBy,
      sortOrder,
      search
    );
  } catch (error) {
    console.error("Error getting user paginated email templates:", error);
    return handleErrorServer(error);
  }
}

export async function getEmailTemplate(
  eventId: string,
  emailTemplateId: string,
  fields: string
): Promise<EmailTemplate | ServerError> {
  try {
    if (!isValidObjectId(eventId)) {
      throw new Error("validationError");
    }
    if (!isValidObjectId(emailTemplateId)) {
      throw new Error("validationError");
    }
    if (!fields) {
      throw new Error("validationError");
    }


    return await getEmailTemplateApi(
      eventId,
      emailTemplateId,
      fields,
    );
  } catch (error) {
    console.error("Error getting email template:", error);
    return handleErrorServer(error);
  }
}

export async function bulkDuplicateEmailTemplates(
  eventId: string,
  emailTemplateIds: string[],
): Promise<EmailTemplate[] | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    return await bulkDuplicateEmailTemplatesApi(eventId, emailTemplateIds);
  } catch (error) {
    console.error("Error bulk duplicating email templates:", error);
    return handleErrorServer(error);
  }
}

export async function deleteEmailTemplate(
  eventId: string,
  emailTemplateId: string
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId)) {
      throw new Error("validationError");
    }
    if (!isValidObjectId(emailTemplateId)) {
      throw new Error("validationError");
    }

    return await deleteEmailTemplateApi(eventId, emailTemplateId);
  } catch (error) {
    console.error("Error deleting email template:", error);
    return handleErrorServer(error);
  }
}

export async function presignDirectUploadHandler(
  eventId: string,
  emailTemplateId: string,
  data: { fileName: string; contentType: string; fileSize: number }
): Promise<{ uploadUrl: string; s3Key: string; fileId: string } | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (!isValidObjectId(emailTemplateId)) throw new Error("validationError");

    return await presignDirectUploadApi(eventId, emailTemplateId, data);
  } catch (error) {
    console.error("Error presigning direct upload:", error);
    return handleErrorServer(error);
  }
}

export async function confirmDirectUploadHandler(
  eventId: string,
  emailTemplateId: string,
  data: { s3Key: string; fileName: string; fileSize: number; contentType: string }
): Promise<DirectUploadFile | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (!isValidObjectId(emailTemplateId)) throw new Error("validationError");

    return await confirmDirectUploadApi(eventId, emailTemplateId, data);
  } catch (error) {
    console.error("Error confirming direct upload:", error);
    return handleErrorServer(error);
  }
}

export async function deleteDirectUploadHandler(
  eventId: string,
  emailTemplateId: string,
  fileId: string
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (!isValidObjectId(emailTemplateId)) throw new Error("validationError");

    return await deleteDirectUploadApi(eventId, emailTemplateId, fileId);
  } catch (error) {
    console.error("Error deleting direct upload:", error);
    return handleErrorServer(error);
  }
}

// ── Template email template handlers ─────────────────────────────────────────

export async function createTemplateEmailTemplateHandler(
  templateId: string,
  emailTemplate: EmailTemplate
): Promise<string | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");

    return await createTemplateEmailTemplateApi(templateId, emailTemplate);
  } catch (error) {
    console.error("Error creating template email template:", error);
    return handleErrorServer(error);
  }
}

export async function getTemplateEmailTemplatesHandler(
  templateId: string,
  fields: string
): Promise<EmailTemplate[] | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    if (!fields) throw new Error("validationError");

    return await getTemplateEmailTemplatesApi(templateId, fields);
  } catch (error) {
    console.error("Error getting template email templates:", error);
    return handleErrorServer(error);
  }
}

export async function getTemplateEmailTemplateHandler(
  templateId: string,
  emailTemplateId: string,
  fields: string
): Promise<EmailTemplate | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    if (!isValidObjectId(emailTemplateId)) throw new Error("validationError");
    if (!fields) throw new Error("validationError");

    return await getTemplateEmailTemplateApi(templateId, emailTemplateId, fields);
  } catch (error) {
    console.error("Error getting template email template:", error);
    return handleErrorServer(error);
  }
}

export async function updateTemplateEmailTemplateHandler(
  templateId: string,
  emailTemplateId: string,
  emailTemplate: Partial<EmailTemplate>
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    if (!isValidObjectId(emailTemplateId)) throw new Error("validationError");
    if (!emailTemplate || Object.keys(emailTemplate).length === 0) throw new Error("validationError");

    return await updateTemplateEmailTemplateApi(templateId, emailTemplateId, emailTemplate);
  } catch (error) {
    console.error("Error updating template email template:", error);
    return handleErrorServer(error);
  }
}

export async function deleteTemplateEmailTemplateHandler(
  templateId: string,
  emailTemplateId: string
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    if (!isValidObjectId(emailTemplateId)) throw new Error("validationError");

    return await deleteTemplateEmailTemplateApi(templateId, emailTemplateId);
  } catch (error) {
    console.error("Error deleting template email template:", error);
    return handleErrorServer(error);
  }
}

// ── Template direct upload handlers ─────────────────────────────────────────

export async function presignTemplateDirectUploadHandler(
  templateId: string,
  emailTemplateId: string,
  data: { fileName: string; contentType: string; fileSize: number }
): Promise<{ uploadUrl: string; s3Key: string; fileId: string } | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    if (!isValidObjectId(emailTemplateId)) throw new Error("validationError");

    return await presignTemplateDirectUploadApi(templateId, emailTemplateId, data);
  } catch (error) {
    console.error("Error presigning template direct upload:", error);
    return handleErrorServer(error);
  }
}

export async function confirmTemplateDirectUploadHandler(
  templateId: string,
  emailTemplateId: string,
  data: { s3Key: string; fileName: string; fileSize: number; contentType: string }
): Promise<DirectUploadFile | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    if (!isValidObjectId(emailTemplateId)) throw new Error("validationError");

    return await confirmTemplateDirectUploadApi(templateId, emailTemplateId, data);
  } catch (error) {
    console.error("Error confirming template direct upload:", error);
    return handleErrorServer(error);
  }
}

export async function deleteTemplateDirectUploadHandler(
  templateId: string,
  emailTemplateId: string,
  fileId: string
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    if (!isValidObjectId(emailTemplateId)) throw new Error("validationError");

    return await deleteTemplateDirectUploadApi(templateId, emailTemplateId, fileId);
  } catch (error) {
    console.error("Error deleting template direct upload:", error);
    return handleErrorServer(error);
  }
}

export async function getTemplates(
  eventId: string,
  emailTemplateId: string
): Promise<
  | {
      mailTemplate: EmailTemplate;
      templates: EmailTemplate[];
    }
  | ServerError
> {
  try {
    if (!isValidObjectId(eventId)) {
      throw new Error("validationError");
    }
    if (!isValidObjectId(emailTemplateId)) {
      throw new Error("validationError");
    }


    const [templatesByEvent, singleTemplate] = await Promise.all([
      getEmailTemplatesByEventApi(
        eventId,
        "templateName,emailTemplateId",
      ),
      getEmailTemplateApi(
        eventId,
        emailTemplateId,
        "primaryLanguage,languages,templateName,craftJs,emailTemplateId,texts,attachments,portalLinkEnabled,portalLinkAttachmentIds,portalLinkExpiryDays,directUploadFiles,contentAreaWidth,contentAreaAlignment,contentAreaBackgroundColor,linkColor,globalFont",
      ),
    ]);

    // Check if either result is a ServerError by checking for the isServerError property
    if (
      typeof templatesByEvent === "object" &&
      templatesByEvent !== null &&
      "isServerError" in templatesByEvent
    ) {
      return templatesByEvent as unknown as ServerError;
    }
    if (
      typeof singleTemplate === "object" &&
      singleTemplate !== null &&
      "isServerError" in singleTemplate
    ) {
      return singleTemplate as unknown as ServerError;
    }

    // Filter out the mailTemplate from templates array
    const filteredTemplates = (templatesByEvent as EmailTemplate[]).filter(
      (template) => template.emailTemplateId !== emailTemplateId
    );

    return {
      mailTemplate: singleTemplate as EmailTemplate,
      templates: filteredTemplates,
    };
  } catch (error) {
    console.error("Error getting email templates:", error);
    return handleErrorServer(error);
  }
}
